import React from "react";
import { ChevronDown } from "lucide-react";
import MarketHeroAvatar from "../../../../components/market/MarketHeroAvatar";
import ContractLineAvatar from "../../../../components/market/ContractLineAvatar";
import { MARKET_TYPES } from "../../../utils/bondPredictHelpers";
import {
  effectiveStatusFor,
  findContractLine,
  formatCoin,
  formatSignedCoin,
  getMarketTitle,
  getPositionOutcomeLabel,
  getStatusMeta,
} from "../../../utils/bondPredictPortfolioHelpers";
import { PortfolioMarketTypeSign } from "./PortfolioListSign";
import { PortfolioHoldingExpand } from "./PortfolioRowExpand";

const trailBarToneForStatus = (tone) => {
  if (tone === "final") return "final";
  if (tone === "pending") return "pending";
  return "live";
};

const PortfolioHoldingsTable = ({
  positions,
  expandedKey,
  onToggleExpand,
  userId,
  onTrade,
  onOpenMarket,
}) => (
  <div className="bp-port-list bp-port-list-holdings" role="list" aria-label="Holdings">
    {positions.map((position) => {
      const market = position?.market || {};
      const marketId = market?._id || position?.market;
      const marketTitle = getMarketTitle(market);
      const isMulti = market?.type === MARKET_TYPES.MULTI_BINARY;
      const contractLine = isMulti
        ? findContractLine(market, position?.outcome?.selectionKey)
        : null;
      const effectiveStatus = effectiveStatusFor(market, contractLine);
      const statusMeta = getStatusMeta(effectiveStatus);
      const outcomeLabel = getPositionOutcomeLabel(position);
      const key = String(
        position?._id || `${marketId}-${position?.outcome?._id || position?.outcome}`
      );
      const realizedPnL = Number(position?.realizedPnL || 0);
      const pnlTone = realizedPnL >= 0 ? "gain" : "loss";
      const shares = Number(position?.shares || 0);
      const invested = Number(position?.invested || 0);
      const expanded = expandedKey === key;
      const trailBarTone = trailBarToneForStatus(statusMeta.tone);
      const trailBarWidth = shares > 0 ? 100 : 0;

      return (
        <article
          key={key}
          className={`bp-port-item bp-port-item-holding bp-port-item-status-${statusMeta.tone}${expanded ? " bp-port-item-open" : ""}`}
          role="listitem"
        >
          <button
            type="button"
            className="bp-port-item-summary"
            aria-expanded={expanded}
            onClick={() => onToggleExpand(key, position)}
          >
            {market?.type === MARKET_TYPES.HEAD_TO_HEAD ? (
              <PortfolioMarketTypeSign market={market} statusTone={statusMeta.tone} />
            ) : isMulti && contractLine ? (
              <ContractLineAvatar
                market={market}
                selectionKey={position?.outcome?.selectionKey}
                label={outcomeLabel}
                size={32}
                className="bp-port-item-avatar"
              />
            ) : (
              <MarketHeroAvatar
                market={market}
                label={marketTitle}
                size={32}
                className="bp-port-item-avatar"
              />
            )}
            <span className="bp-port-item-copy">
              <strong className="bp-port-item-title">{marketTitle}</strong>
              <span className="bp-port-item-sub">
                <span className="bp-port-side-tag bp-port-side-tag-buy">HELD</span>
                <span className="bp-port-contract-chip">{outcomeLabel}</span>
                <span className="bp-port-item-muted tabular-nums">
                  {shares.toFixed(1)} shares
                </span>
                <span className="bp-port-item-muted tabular-nums">
                  {formatCoin(invested)} invested
                </span>
              </span>
            </span>
            <span className="bp-port-item-summary-trail">
              <span className="bp-port-item-trail-head">
                <span className="bp-port-item-trail-metric">
                  <span className="bp-port-item-metric-label">P&amp;L</span>
                  <span
                    className={`bp-port-item-fill tabular-nums bp-port-item-pnl-${pnlTone}`}
                  >
                    {formatSignedCoin(realizedPnL)}
                  </span>
                </span>
                <span className={`bp-port-status-pill bp-port-status-${statusMeta.tone}`}>
                  {statusMeta.label}
                </span>
              </span>
              <span className="bp-port-item-fill-track" aria-hidden>
                <span
                  className={`bp-port-item-fill-track-bar bp-port-fill-${trailBarTone}`}
                  style={{ width: `${trailBarWidth}%` }}
                />
              </span>
            </span>
            <ChevronDown
              size={14}
              className={`bp-port-item-chevron${expanded ? " bp-port-item-chevron-open" : ""}`}
              aria-hidden
            />
          </button>
          {expanded ? (
            <PortfolioHoldingExpand
              position={position}
              userId={userId}
              onTrade={(side) => onTrade?.(side, position)}
              onOpenMarket={onOpenMarket}
            />
          ) : null}
        </article>
      );
    })}
  </div>
);

export default PortfolioHoldingsTable;
